import React, { useEffect, useMemo, useState } from "react";
import AdminLayout from "../../components/AdminLayout";
import { Campaign, UserService } from "../../services/UserService";
import { CampaignService } from "../../services/CampaignService";
import { NotificationService } from "../../services/NotificationService";
import { NotificationTemplate, NotificationTestChannel } from "../../types/Notification";
import { NotificationFrequency, UserRole } from "../../types/enums";
import { AuthService } from "../../services/AuthService";

import { formatNotificationCopy } from "../../utils/formatNotificationCopy";

const frequencyLabels: Record<NotificationFrequency, { label: string; hint?: string }> = {
  [NotificationFrequency.WEEKLY]: { label: "Semanal" },
  [NotificationFrequency.MONTHLY]: { label: "Mensal" },
  [NotificationFrequency.CRON]: { label: "Por meta" },
  [NotificationFrequency.DAILY]: { label: "Diário" },
  [NotificationFrequency.ONCE]: { label: "Único" },
};

const WhatsAppIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" role="presentation" aria-hidden="true">
    <path
      d="M4 4h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H8l-4 4V6a2 2 0 0 1 2-2z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M9.5 9.5c.7-.5 1.7-.4 2.3.2.5.5.6 1.3.3 2-.2.5-.5.9-.7 1.3-.2.3-.3.6-.1.8.1.2.3.3.6.3.5 0 1.3-.3 1.7-.6.6-.5 1-1.2 1.1-2 .1-.8-.2-1.6-.9-2-1.1-.7-2.5-.2-3.1.9z"
      fill="currentColor"
      opacity="0.8"
    />
  </svg>
);

const EmailIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" role="presentation" aria-hidden="true">
    <rect
      x="3.5"
      y="6"
      width="17"
      height="12"
      rx="2.2"
      ry="2.2"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <polyline
      points="4 8.5 12 13.5 20 8.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);


type ActiveTestState = {
  channel: NotificationTestChannel;
  templateId?: string | null;
} | null;

const NotificationControl: React.FC = () => {
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [campaigns, setCampaigns] = useState<Campaign[] | null>(null);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [campaignMenuOpen, setCampaignMenuOpen] = useState(false);
  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [togglingTemplate, setTogglingTemplate] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeTest, setActiveTest] = useState<ActiveTestState>(null);
  const [_testResult, setTestResult] = useState<string | null>(null);
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [testModalMessage, setTestModalMessage] = useState<string>("Testando envio...");
  const [testModalStatus, setTestModalStatus] = useState<"loading" | "done">("loading");
  const dropdownRef = React.useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (testModalOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [testModalOpen]);

  const canMutate = useMemo(() => userRole === UserRole.ADMIN, [userRole]);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const data = await UserService.getUserWithCamaigns();
        const rawRole = (data.role ?? AuthService.getUserRole()) ?? null;
        const normalizedRole = AuthService.normalizeRole(rawRole ? rawRole.toString().trim() : null);
        setUserRole((normalizedRole as UserRole) ?? null);

        let availableCampaigns = data.campaigns && data.campaigns.length > 0 ? data.campaigns : [];
        if (normalizedRole === UserRole.ADMIN) {
          try {
            const allCampaigns = await CampaignService.list();
            if (allCampaigns && allCampaigns.length > 0) {
              availableCampaigns = allCampaigns as unknown as typeof availableCampaigns;
            }
          } catch (innerErr) {
            console.error("Erro ao carregar campanhas para admin, usando campanhas do usuário", innerErr);
          }
        }

        setCampaigns(availableCampaigns);
        if (availableCampaigns.length > 0) {
          const saved = localStorage.getItem("selectedCampaignId");
          const current = saved ? availableCampaigns.find((c: Campaign) => c.id === Number(saved)) : availableCampaigns[0];
          setSelectedCampaign(current || availableCampaigns[0]);
        }
      } catch (err) {
        console.error("Erro ao carregar usuário/campanhas", err);
        setError("Não foi possível carregar suas campanhas.");
      }
    };
    fetchUser();
  }, []);

  useEffect(() => {
    if (selectedCampaign) {
      localStorage.setItem("selectedCampaignId", selectedCampaign.id.toString());
      loadTemplates(selectedCampaign.id);
    }
  }, [selectedCampaign]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setCampaignMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const loadTemplates = async (campaignId?: number, silentError = false): Promise<NotificationTemplate[] | null> => {
    if (campaignId === undefined || campaignId === null) {
      setTemplates([]);
      return null;
    }
    try {
      setLoading(true);
      const response = await NotificationService.listTemplates(campaignId);
      setTemplates(response);
      setError(null);
      return response;
    } catch (err: any) {
      console.error("Erro ao listar notificações", err);
      if (!silentError) {
        if (err?.response?.status === 404) {
          setTemplates([]);
          setError("Endpoint de notificações não encontrado neste ambiente (verifique VITE_API_URL).");
        } else {
          setError("Erro ao carregar notificações");
        }
      }
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateToggle = async (template: NotificationTemplate, nextActive: boolean) => {
    if (!canMutate || !selectedCampaign) return;
    setTogglingTemplate(template.template_id);
    setError(null);
    const previousTemplates = [...templates];
    setTemplates((prev) =>
      prev.map((t) =>
        t.template_id === template.template_id ? { ...t, available: nextActive } : t
      )
    );
    try {
      await NotificationService.setTemplateStatus(template.template_id, {
        campaign_id: selectedCampaign.id,
        active: nextActive,
      });
      await loadTemplates(selectedCampaign.id);
    } catch (err) {
      console.error("Erro ao alternar notificação", err);
      const refreshed = await loadTemplates(selectedCampaign.id, true);
      const currentStatus = refreshed?.find((t) => t.template_id === template.template_id)?.available;
      if (currentStatus === nextActive) {
        setError(null);
      } else {
        setTemplates(previousTemplates);
        setError("Não foi possível salvar o status do insight.");
      }
    } finally {
      setTogglingTemplate(null);
    }
  };

  const onSelectCampaign = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
  };

  const activeCount = useMemo(() => templates.filter((n) => n.available).length, [templates]);

  const handleTestSend = async (channel: NotificationTestChannel, templateId?: string) => {
    if (!canMutate || !selectedCampaign) return;
    const prettyChannel = channel === "EMAIL" ? "Email" : "WhatsApp";
    setActiveTest({ channel, templateId: templateId ?? null });
    setTestResult(null);
    setError(null);
    setTestModalOpen(true);
    setTestModalStatus("loading");
    setTestModalMessage(`Testando envio via ${prettyChannel}...`);
    try {
      const result = await NotificationService.sendTest(selectedCampaign.id, templateId, channel);
      const label = result.success ? "Enviado" : "Falhou";
      const cleanedDetail = result.detail ? formatNotificationCopy(result.detail) : "";
      const detailSegment = cleanedDetail ? `: ${cleanedDetail}` : "";
      const msg = `${label} via ${prettyChannel}${detailSegment}`;
      setTestResult(msg);
      setTestModalMessage(msg);
      setTestModalStatus("done");
    } catch (err: any) {
      console.error("Erro ao testar envio", err);
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Não foi possível enviar a notificação de teste.";
      const cleanedDetail = formatNotificationCopy(detail);
      setTestResult(cleanedDetail);
      setTestModalMessage(cleanedDetail);
      setTestModalStatus("done");
    } finally {
      setActiveTest(null);
    }
  };

  return (
    <AdminLayout>
      <div className="notifications-page admin-control">
        <div className="notifications-container">
          <div className="page-head">
            <div className="title-block">
              <p className="eyebrow">Painel do Admin</p>
              <h2>Controle de Notificações</h2>
              <p>Ative ou desative cada insight para todos os usuários da campanha.</p>
            </div>
            <div className="status-chip">
              <span className="dot" />
              {activeCount} ativos
            </div>
            {!canMutate && <span className="read-only-tag">Restrito a administradores</span>}
          </div>

          <div className="control-bar">
            <div className="field campaign-field" ref={dropdownRef}>
              <label>Campanha</label>
              <button
                type="button"
                className="campaign-trigger"
                onClick={() => setCampaignMenuOpen((prev) => !prev)}
                aria-haspopup="listbox"
                aria-expanded={campaignMenuOpen}
              >
                <span className="trigger-label">{selectedCampaign?.name || "Selecionar campanha"}</span>
                <span className="trigger-chevron">▾</span>
              </button>
              {campaignMenuOpen && (
                <div className="campaign-menu" role="listbox">
                  {campaigns && campaigns.length > 0 ? (
                    campaigns.map((campaign) => (
                      <button
                        key={campaign.id}
                        className={`campaign-item ${selectedCampaign?.id === campaign.id ? "active" : ""}`}
                        onClick={() => {
                          onSelectCampaign(campaign);
                          setCampaignMenuOpen(false);
                        }}
                        role="option"
                        aria-selected={selectedCampaign?.id === campaign.id}
                      >
                        {campaign.name}
                      </button>
                    ))
                  ) : (
                    <div className="campaign-empty">Sem campanhas disponíveis</div>
                  )}
                </div>
              )}
            </div>
            {loading && <span className="muted">Carregando catálogos...</span>}
          </div>

          <section className="insight-catalog">
            <div className="list-head">
              <div>
                <h3>Catálogo de insights</h3>
                <p className="muted-dark on-light">Ative o overview desejado diretamente no card.</p>
              </div>
              {loading && <span className="muted">Carregando...</span>}
            </div>
            <div className="insight-grid">
              {templates.map((template) => {
                const freq = frequencyLabels[template.default_frequency] ?? { label: "Personalizado" };
                const isAvailable = template.available ?? false;
                const isSaving = togglingTemplate === template.template_id;
                const isWhatsAppTesting =
                  activeTest?.channel === "WHATSAPP" && activeTest?.templateId === template.template_id;
                const isEmailTesting =
                  activeTest?.channel === "EMAIL" && activeTest?.templateId === template.template_id;
                const disableTests = !canMutate || !selectedCampaign || Boolean(activeTest);
                return (
                  <div key={template.template_id} className={`insight-card ${isAvailable ? "active" : ""}`}>
                  <div className="card-top">
                    <div className="card-meta">
                      <span className="pill strong">{freq.label}</span>
                      {freq.hint && <span className="pill muted">{freq.hint}</span>}
                      <label className={`toggle ${isAvailable ? "on" : "off"}`}>
                        <input
                          type="checkbox"
                          checked={isAvailable}
                          disabled={!canMutate || !selectedCampaign || isSaving}
                          onChange={() => handleTemplateToggle(template, !isAvailable)}
                        />
                        <span className="slider" />
                      </label>
                    </div>
                  </div>
                    <div className="test-row">
                      <span className="test-label">Clique para testar:</span>
                      <div className="test-actions">
                        <button
                          type="button"
                          className={`test-send-button small whatsapp ${isWhatsAppTesting ? "loading" : ""}`}
                          onClick={() => handleTestSend("WHATSAPP", template.template_id)}
                          disabled={disableTests}
                          title="Envia um teste para o admin logado via WhatsApp"
                        >
                          <span className="test-icon">
                            <WhatsAppIcon />
                          </span>
                          <span className="test-label">WhatsApp</span>
                        </button>
                        <button
                          type="button"
                          className={`test-send-button small email ${isEmailTesting ? "loading" : ""}`}
                          onClick={() => handleTestSend("EMAIL", template.template_id)}
                          disabled={disableTests}
                          title="Envia um teste para o admin logado via email"
                        >
                          <span className="test-icon">
                            <EmailIcon />
                          </span>
                          <span className="test-label">Email</span>
                        </button>
                      </div>
                    </div>
                    <div className="card-head">
                      <h4>{template.title}</h4>
                    </div>
                    <p>{formatNotificationCopy(template.description)}</p>
                  </div>
                );
              })}
            </div>
          </section>

          {error && <div className="error-box">{error}</div>}
          {testModalOpen && (
            <div className="modal-overlay" role="alertdialog" aria-live="assertive">
              <div className="modal-card">
                <h4>{testModalStatus === "loading" ? "Testando envio..." : "Resultado do teste"}</h4>
                <p>{testModalMessage}</p>
                <div className="modal-actions">
                  {testModalStatus === "loading" ? (
                    <span className="spinner">⏳</span>
                  ) : (
                    <button type="button" onClick={() => setTestModalOpen(false)}>
                      Fechar
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default NotificationControl;
